import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { MessageCircle, X } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const ChatNotificationWidget = () => {
    const navigate = useNavigate();
    const [currentUser, setCurrentUser] = useState(null);
    const [notifications, setNotifications] = useState([]); // Array of notification objects
    const [isExpanded, setIsExpanded] = useState(false);
    const [joinedChannels, setJoinedChannels] = useState(new Map());
    const popupTimeoutRef = useRef(null);

    // Fetch current user, joined channels, and unread messages
    useEffect(() => {
        const fetchUserAndChannels = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;
            setCurrentUser(user);

            // Fetch channels user is a member of
            const { data: members } = await supabase
                .from('team_chat')
                .select('channel_id')
                .eq('record_type', 'member')
                .eq('user_id', user.id);

            if (members && members.length > 0) {
                const channelIds = members.map(m => m.channel_id);
                
                // Fetch names for these channels
                const { data: channels } = await supabase
                    .from('team_chat')
                    .select('id, channel_name')
                    .in('id', channelIds)
                    .eq('record_type', 'channel');

                if (channels) {
                    const channelMap = new Map();
                    channels.forEach(ch => channelMap.set(ch.id, ch.channel_name));
                    setJoinedChannels(channelMap);

                    // Fetch unread messages for all joined channels
                    await fetchUnreadMessages(user.id, channelIds, channelMap);
                }
            }
        };

        fetchUserAndChannels();
    }, []);

    // Fetch unread messages since last read
    const fetchUnreadMessages = async (userId, channelIds, channelMap) => {
        try {
            // Get read status for all channels
            const { data: readStatuses } = await supabase
                .from('chat_read_status')
                .select('channel_id, last_read_at')
                .eq('user_id', userId)
                .in('channel_id', channelIds);

            const readStatusMap = new Map();
            if (readStatuses) {
                readStatuses.forEach(rs => readStatusMap.set(rs.channel_id, rs.last_read_at));
            }

            // Fetch unread messages for each channel
            const unreadNotifications = [];
            
            for (const channelId of channelIds) {
                const lastReadAt = readStatusMap.get(channelId);
                
                // Build query for messages in this channel
                let query = supabase
                    .from('team_chat')
                    .select('id, message_text, created_by, created_at, channel_id')
                    .eq('record_type', 'message')
                    .eq('channel_id', channelId)
                    .neq('created_by', userId) // Exclude user's own messages
                    .order('created_at', { ascending: false })
                    .limit(10); // Limit to last 10 unread per channel

                // If we have a last read timestamp, only get newer messages
                if (lastReadAt) {
                    query = query.gt('created_at', lastReadAt);
                }

                const { data: messages } = await query;

                if (messages && messages.length > 0) {
                    // Fetch sender names for all messages
                    const senderIds = [...new Set(messages.map(m => m.created_by))];
                    const { data: users } = await supabase
                        .from('users')
                        .select('id, name')
                        .in('id', senderIds);

                    const userMap = new Map();
                    if (users) {
                        users.forEach(u => userMap.set(u.id, u.name));
                    }

                    // Create notifications
                    messages.forEach(msg => {
                        unreadNotifications.push({
                            id: msg.id,
                            text: msg.message_text,
                            sender: userMap.get(msg.created_by) || 'Someone',
                            channel: channelMap.get(msg.channel_id) || 'Unknown Channel',
                            channelId: msg.channel_id,
                            timestamp: new Date(msg.created_at)
                        });
                    });
                }
            }

            // Sort all notifications by timestamp (newest first) and add to state
            unreadNotifications.sort((a, b) => b.timestamp - a.timestamp);
            setNotifications(unreadNotifications);

        } catch (error) {
            console.error('Error fetching unread messages:', error);
        }
    };

    // Subscribe to real-time messages
    useEffect(() => {
        if (!currentUser) return;

        const subscription = supabase
            .channel('global-chat-notifications')
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'team_chat',
                    filter: 'record_type=eq.message'
                },
                async (payload) => {
                    const newMessage = payload.new;

                    // Ignore own messages
                    if (newMessage.user_id === currentUser.id) return;

                    // Ignore messages from channels user is not participating in
                    // Note: We need to re-verify if strict membership check is needed or if 'public' channels count
                    // For now, let's assume if we are in the list, we notify. 
                    // If the list is empty (initial load delay), we might miss some, but usually ok.
                    // Better logic: Fetch the channel details to see if it's public? 
                    // For simplicity/performance: check against joinedChannels list.
                    // However, if it's a new public channel I haven't joined?
                    // The user requirement says: "only if the persn is member in the channal"

                    // Check membership
                    let isMember = joinedChannels.has(newMessage.channel_id);
                    let channelName = joinedChannels.get(newMessage.channel_id);

                    // If not in local map, double check DB for membership
                    if (!isMember) {
                        const { data } = await supabase
                            .from('team_chat')
                            .select('id')
                            .eq('record_type', 'member')
                            .eq('channel_id', newMessage.channel_id)
                            .eq('user_id', currentUser.id)
                            .maybeSingle(); // Use maybeSingle to avoid error on null

                        if (data) {
                            isMember = true;
                            // We are a member, so we need to fetch the name now
                            // We will do this in the next block to unify logic
                        }
                    }

                    if (isMember) {
                        // If we don't have the name yet (either from map or it was missing), fetch it
                        if (!channelName) {
                            const { data: ch } = await supabase
                                .from('team_chat')
                                .select('channel_name')
                                .eq('id', newMessage.channel_id)
                                .eq('record_type', 'channel')
                                .single();

                            if (ch?.channel_name) {
                                channelName = ch.channel_name;
                                // Update local cache so we don't fetch again
                                setJoinedChannels(prev => {
                                    const newMap = new Map(prev);
                                    newMap.set(newMessage.channel_id, channelName);
                                    return newMap;
                                });
                            }
                        }

                        // Final fallback
                        if (!channelName) channelName = 'Unknown Channel';

                        // Fetch sender name
                        const { data: senderData } = await supabase
                            .from('users')
                            .select('name')
                            .eq('id', newMessage.user_id)
                            .single();

                        const senderName = senderData?.name || 'Someone';

                        // Add new notification to list (avoid duplicates)
                        const newNotification = {
                            id: newMessage.id,
                            text: newMessage.message_text || newMessage.content || '',
                            sender: senderName,
                            channel: channelName,
                            channelId: newMessage.channel_id,
                            timestamp: new Date(newMessage.created_at || Date.now())
                        };

                        setNotifications(prev => {
                            // Check if notification already exists
                            if (prev.some(n => n.id === newNotification.id)) {
                                return prev;
                            }
                            return [newNotification, ...prev];
                        });
                        setIsExpanded(true);

                        // Auto-collapse after 5 seconds to the bubble
                        if (popupTimeoutRef.current) clearTimeout(popupTimeoutRef.current);
                        popupTimeoutRef.current = setTimeout(() => {
                            setIsExpanded(false);
                        }, 5000);
                    }
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(subscription);
            if (popupTimeoutRef.current) clearTimeout(popupTimeoutRef.current);
        };
    }, [currentUser, joinedChannels]);

    // If no unread messages, don't render anything? 
    // User said "create a count... and shrink to one small circle"
    // So it should be visible if count > 0.
    const unreadCount = notifications.length;

    if (unreadCount === 0) return null;

    const handleClick = (notif) => {
        // Navigate to chat
        if (notif?.channelId) {
            // Save to local storage for TeamChat to pick up
            localStorage.setItem('lastSelectedChannel', notif.channelId);
            
            // Mark channel as read up to now
            markChannelAsRead(notif.channelId);
            
            navigate('/team-chat');

            // Remove clicked notification and all other notifications from that channel
            setNotifications(prev => prev.filter(n => n.channelId !== notif.channelId));
        } else {
            navigate('/team-chat');
        }
    };

    const markChannelAsRead = async (channelId) => {
        if (!currentUser) return;
        
        try {
            await supabase.rpc('update_chat_read_status', {
                p_user_id: currentUser.id,
                p_channel_id: channelId,
                p_timestamp: new Date().toISOString()
            });
        } catch (error) {
            console.error('Error marking channel as read:', error);
        }
    };

    const handleDismiss = async (e, notifId) => {
        e.stopPropagation();
        
        // Find the notification and mark its channel as read up to that message's timestamp
        const notif = notifications.find(n => n.id === notifId);
        if (notif && currentUser) {
            try {
                await supabase.rpc('update_chat_read_status', {
                    p_user_id: currentUser.id,
                    p_channel_id: notif.channelId,
                    p_timestamp: notif.timestamp.toISOString()
                });
            } catch (error) {
                console.error('Error updating read status:', error);
            }
        }
        
        setNotifications(prev => prev.filter(n => n.id !== notifId));
        if (notifications.length <= 1) setIsExpanded(false);
    };

    const handleClearAll = async (e) => {
        e.stopPropagation();
        
        // Mark all channels as read
        if (currentUser) {
            const channelTimestamps = new Map();
            
            // Get the latest timestamp for each channel
            notifications.forEach(notif => {
                const existing = channelTimestamps.get(notif.channelId);
                if (!existing || notif.timestamp > existing) {
                    channelTimestamps.set(notif.channelId, notif.timestamp);
                }
            });
            
            // Update read status for each channel
            for (const [channelId, timestamp] of channelTimestamps.entries()) {
                try {
                    await supabase.rpc('update_chat_read_status', {
                        p_user_id: currentUser.id,
                        p_channel_id: channelId,
                        p_timestamp: timestamp.toISOString()
                    });
                } catch (error) {
                    console.error('Error updating read status:', error);
                }
            }
        }
        
        setNotifications([]);
        setIsExpanded(false);
    };

    return (
        <div
            className="fixed bottom-24 right-4 lg:bottom-6 lg:right-6 z-50 flex flex-col items-end pointer-events-none"
        >
            {/* Expanded Popup Message List */}
            <div
                className={`pointer-events-auto flex flex-col-reverse gap-2 mb-4 transition-all duration-300 transform origin-bottom-right max-h-[60vh] overflow-y-auto scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent pr-2 ${isExpanded ? 'scale-100 opacity-100 translate-y-0' : 'scale-90 opacity-0 translate-y-10 h-0 overflow-hidden'
                    }`}
                onMouseEnter={() => {
                    if (popupTimeoutRef.current) clearTimeout(popupTimeoutRef.current);
                    setIsExpanded(true);
                }}
                onMouseLeave={() => {
                    popupTimeoutRef.current = setTimeout(() => {
                        setIsExpanded(false);
                    }, 2000);
                }}
            >
                {notifications.map((notif) => (
                    <div
                        key={notif.id}
                        onClick={() => handleClick(notif)}
                        className="glass-card p-4 rounded-xl shadow-2xl border border-white/10 bg-gray-900/95 backdrop-blur-xl cursor-pointer hover:bg-gray-800/90 transition-colors w-80 relative group"
                    >
                        <div className="flex justify-between items-start gap-3">
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-blue-400 mb-0.5 truncate">
                                    New message from {notif.sender}
                                </p>
                                <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider mb-1 truncate">
                                    #{notif.channel}
                                </p>
                                <p className="text-xs text-gray-300 line-clamp-2 break-words">
                                    {notif.text}
                                </p>
                            </div>
                            <button
                                onClick={(e) => handleDismiss(e, notif.id)}
                                className="text-gray-500 hover:text-white -mr-1 -mt-1 p-1 rounded-full hover:bg-white/10 transition-colors"
                            >
                                <X size={14} />
                            </button>
                        </div>
                        <div className="mt-2 text-[10px] text-gray-600 text-right">
                            {formatDistanceToNow(notif.timestamp, { addSuffix: true })}
                        </div>
                    </div>
                ))}

                {notifications.length > 2 && (
                    <div className="flex justify-center pb-2">
                        <button
                            onClick={handleClearAll}
                            className="bg-black/50 hover:bg-black/70 text-gray-400 hover:text-white text-xs px-3 py-1 rounded-full backdrop-blur-md transition-colors border border-white/5"
                        >
                            Clear All
                        </button>
                    </div>
                )}
            </div>

            {/* Floating Action Button (The "Small Circle") */}
            <div
                onClick={() => {
                    if (notifications.length > 0) setIsExpanded(!isExpanded);
                    else navigate('/team-chat');
                }}
                className="pointer-events-auto relative group cursor-pointer"
                onMouseEnter={() => setIsExpanded(true)}
            >
                <div className="w-12 h-12 rounded-full bg-blue-600 hover:bg-blue-500 text-white flex items-center justify-center shadow-lg shadow-blue-500/30 transition-all duration-300 hover:scale-110">
                    <MessageCircle size={24} />
                </div>

                {/* Unread Count Badge */}
                {unreadCount > 0 && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center border-2 border-[#0a0e1a]">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChatNotificationWidget;
